import {ChangeDetectionStrategy, Component, computed, forwardRef, Injectable, input, signal} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AgendaelementId, AgendaelementModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {AvatarKomponent} from "web-ng2/delt/komponenter/avatar/avatar";
import {FormaterPlainTimePipe} from "felles-ng2/pipes/FormaterPlainTimePipe";
import {FormaterDurationPipe} from "felles-ng2/pipes/FormaterDurationPipe";
import {TittelVerdiSamlingKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi-samling";
import {TittelKomponent, TittelVerdiKomponent, VerdiKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi";
import {TranslateModule} from "@ngx-translate/core";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {erFavorittHosBruker} from "felles-ng2/modell/brukerModellHjelpere";
import {NgTemplateOutlet} from "@angular/common";
import {ReactiveFormsModule} from "@angular/forms";
import {ZoneddatetimeSomPlainTimePipe} from "felles-ng2/pipes/ZonedDateTimeSomPlainTimePipe";
import {AgendaelementModellDLM, SakDatatjeneste} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {SaksdokumentDatatjeneste} from "felles-ng2/tjenester/datalager/SaksdokumentDatatjeneste";
import {map, switchMap} from "rxjs/operators";
import {antallDokumenterLest, DokumenterInformasjon} from "felles-ng2/modell/dokumentModellHjelpere";
import {Observable} from "rxjs";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {KommentarDatatjeneste} from "felles-ng2/tjenester/datalager/KommentarDatatjeneste";
import {OppgaveDatatjeneste} from "felles-ng2/tjenester/datalager/OppgaveDatatjeneste";
import {antallOppgaverForfalt, OppgaverInformasjon} from "felles-ng2/modell/oppgaveModellHjelpere";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {BeslutningDatatjeneste} from "felles-ng2/tjenester/datalager/BeslutningDatatjeneste";
import {MatTooltip} from "@angular/material/tooltip";
import {sakVarighetSomDuration} from "felles-ng2/modell/sakModellHjelpere";
import {IkonMedTellerKomponent} from "web-ng2/delt/komponenter/ikon-med-teller/ikon-med-teller";
import {HtmlFormatertKomponent} from "web-ng2/delt/komponenter/html-formatert/html-formatert";
import {tomAgendaelementModell} from "felles-ng2/modell/tommeModeller";

const debug = initDebugger('SakListevisningKomponent');


@Component({
    selector: 'sak-vis-kompakt',
    templateUrl: 'sak-vis-kompakt.html',
    styleUrl: 'sak-vis-kompakt.scss',
    host: {
        'data-testid': 'sak',
        '[class.er-pause]': 'sak().erPause'
    },
    imports: [
        ReactiveFormsModule,
        MatIcon,
        MatIconButton,
        NgTemplateOutlet,
        FormaterPlainTimePipe,
        FormaterDurationPipe,
        ZoneddatetimeSomPlainTimePipe,
        AvatarKomponent,
        TittelVerdiSamlingKomponent,
        TittelVerdiKomponent,
        TittelKomponent,
        VerdiKomponent,
        TranslateModule,
        MatTooltip,
        IkonMedTellerKomponent,
        forwardRef(() => HtmlFormatertKomponent), //må bruke forwardRef for å unngå sirkulær avhengighet SakVisKompaktKomponent -> HtmlFormatertKomponent -> Lenkepanel -> RiktekstRedigeringLenkeRedigerKomponent -> SakVisKompaktKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SakVisKompaktKomponent {

    sakId = input.required<AgendaelementId>();

    sak = signal(tomAgendaelementModell() as AgendaelementModellDLM);
    varighet = computed(() => sakVarighetSomDuration(this.sak()));
    erFavoritt = computed(() => erFavorittHosBruker(this.sak().favoritter ?? [], this.kontekst));
    antallDokumenter = toSignal(toObservable(this.sak).pipe(switchMap(sak => this.avhengigheter.antallDokumenter(sak.id))), {initialValue: {antallUleste: 0, antallTotalt: 0}});
    antallOppgaver = toSignal(toObservable(this.sak).pipe(switchMap(sak => this.avhengigheter.antallOppgaver(sak.id))), {initialValue: {antallForfalt: 0, antallTotalt: 0}});
    antallBeslutninger = toSignal(toObservable(this.sak).pipe(switchMap(sak => this.avhengigheter.antallBeslutninger(sak.id))), {initialValue: 0});
    antallKommentarer = toSignal(toObservable(this.sak).pipe(switchMap(sak => this.avhengigheter.antallKommentarer(sak.id))), {initialValue: 0});

    constructor(private avhengigheter: SakVisKompaktKomponentAvhengigheter,
                private kontekst: BrukerkontekstTilbyder,
                private navigasjon: Navigasjon) {
        toObservable(this.sakId)
          .pipe(switchMap(id => this.avhengigheter.hentSak(id)),
            takeUntilDestroyed())
          .subscribe(sak => this.sak.set(sak));
    }

    gaaTilSaksdetaljer() {
        this.navigasjon.gåTilMøteOgSak(this.sak().moete.id, this.sak().id);
    }

    //private hjelpemetoder

}

@Injectable()
export class SakVisKompaktKomponentAvhengigheter {

    constructor(private sakDatatjeneste: SakDatatjeneste,
                private saksdokumentDatatjeneste: SaksdokumentDatatjeneste,
                private oppgaveDatatjeneste: OppgaveDatatjeneste,
                private beslutningDatatjeneste: BeslutningDatatjeneste,
                private kommentarDatatjeneste: KommentarDatatjeneste,
                private klokke: Klokke) {
    }


    antallDokumenter(sakId: AgendaelementId): Observable<DokumenterInformasjon> {
        return this.saksdokumentDatatjeneste.finnTilknyttetSakFraLokallager(sakId)
          .pipe(map(dokumenter => antallDokumenterLest(dokumenter)));
    }

    antallOppgaver(sakId: AgendaelementId): Observable<OppgaverInformasjon> {
        return this.oppgaveDatatjeneste.finnTilknyttetSakFraLokallager(sakId)
          .pipe(map(oppgaver => antallOppgaverForfalt(oppgaver, this.klokke)));
    }

    antallBeslutninger(sakId: AgendaelementId): Observable<number> {
        return this.beslutningDatatjeneste.finnTilknyttetSakFraLokallager(sakId)
          .pipe(map(beslutninger => beslutninger.length));
    }

    antallKommentarer(sakId: AgendaelementId): Observable<number> {
        return this.kommentarDatatjeneste.finnTilknyttetFraLokallager(sakId)
          .pipe(map(kommentarer => kommentarer.length));
    }

    hentSak(id: AgendaelementId): Observable<AgendaelementModellDLM> {
        return this.sakDatatjeneste.hent(id);
    }
}
